import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { object, string } from "yup";
import { useHistory } from "react-router-dom";
import AdminFormModal from "../AdminFormModal";
import { Form, Button, Row, Space } from "@masterteam/mt-design-react";
import { ReactComponent as ProgramIcon } from "../../assets/images/programs-icon.svg";
import { addToast } from "../../helpers";
import api from "../../providers/api";

import "./style.scss";

const { Field } = Form;

const validationSchema = object().shape({
  EnglishName: string().required(" "),
  ArabicName: string().required(" "),
  EnglishDescription: string().required(" "),
  ArabicDescription: string().required(" "),
  OwnerModel: object().nullable().required(" "),
  SponsorModel: object().nullable().required(" "),
});

const CreateProgramForm = ({ data = null, closeModal, getPrograms }) => {
  const intl = useIntl();
  const history = useHistory();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [initialValues, setInitialValues] = useState(
    data || {
      EnglishName: "",
      ArabicName: "",
      EnglishDescription: "",
      ArabicDescription: "",
      OwnerModel: null,
      SponsorModel: null,
    }
  );

  const getUsers = () => {
    api.user
      .getAll()
      .then((res) => {
        const { StatusCode, Data } = res;
        if (StatusCode === "Success") {
          setUsers(Data);
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (data) {
      setInitialValues(data);
    }
    getUsers();
  }, []);

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      const body = {
        ...values,
      };

      const service = data ? "update" : "create";
      const res = await api.programs[service](body);
      const { StatusCode } = res;

      if (StatusCode === "Success") {
        addToast.success("toast.successSave");
        closeModal();
        history.push("/splus/programs");
        if (data) getPrograms();
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminFormModal
      title={intl.formatMessage({ id: "create-program" })}
      Icon={ProgramIcon}
      isLoading={loading}
      onClose={closeModal}
    >
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, isSubmitting, isValid, setFieldValue }) => (
          <>
            <Space
              direction="vertical"
              size="medium"
              className="create-program-form-body"
            >
              <Space
                direction="vertical"
                size="medium"
                className="form-container"
              >
                <Row size="small">
                  <Field
                    name="EnglishName"
                    label={intl.formatMessage({
                      id: "program.english-title",
                    })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "program.english-title",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                  <Field
                    name="ArabicName"
                    label={intl.formatMessage({ id: "program.arabic-title" })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "program.arabic-title",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row size="small">
                  <Field
                    type="textarea"
                    name="EnglishDescription"
                    label={intl.formatMessage({
                      id: "program.english-description",
                    })}
                    inputProps={{
                      placeholder: "Objective Description (En)",
                      placeholder: intl.formatMessage({
                        id: "program.english-description",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                  <Field
                    type="textarea"
                    name="ArabicDescription"
                    label={intl.formatMessage({
                      id: "program.arabic-description",
                    })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "program.arabic-description",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row size="small">
                  <Field
                    type="select"
                    name="OwnerModel"
                    label={intl.formatMessage({
                      id: "program.owner",
                    })}
                    inputProps={{
                      options: users,
                      getOptionLabel: (el) => el.DisplayName,
                      getOptionValue: (el) => el?.UserName,
                      menuPlacement: "top",
                      placeholder: intl.formatMessage({
                        id: "program.owner",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />

                  <Field
                    type="select"
                    name="SponsorModel"
                    label={intl.formatMessage({
                      id: "program.sponsor",
                    })}
                    inputProps={{
                      options: users,
                      getOptionLabel: (el) => el.DisplayName,
                      getOptionValue: (el) => el?.UserName,
                      menuPlacement: "top",
                      placeholder: intl.formatMessage({
                        id: "program.sponsor",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>
              </Space>
              <Row justify="end" className="form-footer">
                <Button
                  loading={isSubmitting}
                  disabled={!isValid}
                  variant="primary"
                  type="submit"
                  fixed
                >
                  {intl.formatMessage({
                    id: data ? "common.update" : "common.add",
                  })}
                </Button>
              </Row>
            </Space>
          </>
        )}
      </Form>
    </AdminFormModal>
  );
};

export default CreateProgramForm;
